import { css } from '@emotion/react';
import { useQuery } from 'react-query';
import { useState, FC, useEffect } from 'react';
import { useParams, Route, RouteComponentProps, Link } from 'react-router-dom';
import { Spinner } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import Page from '../Page';
import { TimeRange } from '../../types';
import { colors, spacing, calculateChangeColor, fontStyles } from '../../styles';
import Header from '../Header';
import { getBasicInvestment, getCharts, getTechnicalsForInterval } from '../../apis/backend';
import { reportError } from '../../utils/reporting';
import ErrorSection from '../ErrorSection';
import TimeRangeRadios from '../TimeRangeRadios';
import FeedbackFooter from '../FeedbackFooter';
import BottomLineExplanationTakeover from '../BottomLineExplanationTakeover';
import FeedbackTakeover from '../FeedbackTakeover';
import { useRatingDialog } from '../../hooks/useRatingDialog';
import * as stores from '../../stores';
import RadioButton from '../RadioButton';
import { appsflyerEvents } from '../../utils/appsflyerEvents';
import { buildDateText, buildTechnicalDateText } from './Chart/utils';
import Chart, { ChartNewsData } from './Chart';
import TechnicalChart from './TechnicalChart';
import BasicInfo from './BasicInfo';
import Overview from './Overview';
import Related from './Related';
import Holdings from './Holdings';
import KeyStatsPage from './KeyStatsPage';
// Risk factors feature removed
import BottomLine from './BottomLine';
import Info from './Info';
import Insights from './Insights';
import AllInsightsPage from './Insights/AllInsightsPage';
import AllKeyEventsPage from './AllKeyEventsPage';
import TranscriptPage from './TranscriptPage';
import EarningsTranscripts from './EarningsTranscripts';
import KeyEvents from './KeyEvents';
import wiimGraphic from './wiim.svg';

type AllowedTimeRange = TimeRange;

const SymbolPage: FC<RouteComponentProps> = ({ match }) => {
  const { t, i18n } = useTranslation();
  const { symbol } = useParams<{ symbol?: string }>();
  const baseSymbol = symbol?.toUpperCase();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    // Track symbol view with AppsFlyer
    if (baseSymbol) {
      appsflyerEvents.research.symbolViewed(baseSymbol, 'direct').catch(console.error);
    }
  }, [baseSymbol]);

  const [isBottomLineExplanationShown, setIsBottomLineExplanationShown] = useState(false);
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const isSubscribed = purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;

  const basicInvestmentQuery = useQuery(['investments', baseSymbol, i18n.language], async () => {
    return baseSymbol ? getBasicInvestment(baseSymbol) : undefined;
  });
  const chartsQuery = useQuery(['investments', 'charts', baseSymbol], async () => {
    try {
      return baseSymbol ? await getCharts(baseSymbol) : undefined;
    } catch (error) {
      console.error('Error fetching chart data:', error);
      throw error; // Let react-query handle the error state
    }
  });
  const [selectedTimeRange, setSelectedTimeRange] = useState<AllowedTimeRange>(TimeRange.months1);
  const [selectedNewsChartElement, setSelectedNewsChartElement] = useState<ChartNewsData | undefined>();
  const [scannedChartElement, setScannedChartElement] = useState<ChartNewsData | undefined>();
  const [scannedTechnicalElement, setScannedTechnicalElement] = useState<any>();
  const [showTechnicalChart, setShowTechnicalChart] = useState(false);
  const technicalsQuery = useQuery(
    ['investments', 'technicals', baseSymbol, selectedTimeRange],
    async () => (baseSymbol ? getTechnicalsForInterval(baseSymbol, selectedTimeRange) : undefined),
    { enabled: !!baseSymbol && showTechnicalChart, keepPreviousData: true },
  );

  // Helper function to get time ranges, excluding 5Y for crypto
  const getAvailableTimeRanges = () => {
    const isCrypto = basicInvestmentQuery.data?.category === 'CRYPTO';
    const baseTimeRanges = [
      TimeRange.days1,
      TimeRange.weeks1,
      TimeRange.months1,
      TimeRange.months3,
      TimeRange.months6,
      TimeRange.ytd,
      TimeRange.years1,
      ...(isCrypto ? [] : [TimeRange.years5]),
    ];

    // Use chartsQuery to determine which time ranges have data
    // Return [] during loading to avoid visual jump when data loads
    return chartsQuery.data ? baseTimeRanges.filter((time) => chartsQuery.data?.[time]?.length) : [];
  };

  const changeRatioMap: { [index in AllowedTimeRange]: number } | undefined = basicInvestmentQuery.data && {
    '1d': basicInvestmentQuery.data.changePercent1d,
    '1w': basicInvestmentQuery.data.changePercent1w,
    '1m': basicInvestmentQuery.data.changePercent1m,
    '3m': basicInvestmentQuery.data.changePercent3m,
    '6m': basicInvestmentQuery.data.changePercent6m,
    ytd: basicInvestmentQuery.data.changePercentYtd,
    '1y': basicInvestmentQuery.data.changePercent1y,
    '5y': basicInvestmentQuery.data.changePercent5y,
  };

  const changeRatio = selectedTimeRange && changeRatioMap ? changeRatioMap[selectedTimeRange] : undefined;
  const selectedChart: ChartElement[] = (selectedTimeRange && chartsQuery.data?.[selectedTimeRange]) || [];

  //--------------------RATING DIALOG-----------------------//

  const { isFeedbackTakeoverShown, setIsFeedbackTakeoverShown, triggerRatingDialog } = useRatingDialog({
    daysToWait: 3,
  });

  useEffect(() => {
    const count = parseInt(localStorage.getItem('symbol_view_count') || '0', 10) + 1;
    localStorage.setItem('symbol_view_count', count.toString());
    return () => {
      if (count === 2) {
        triggerRatingDialog();
      }
    };
  }, [triggerRatingDialog]);
  //--------------------RATING DIALOG-----------------------//

  if (!symbol || !baseSymbol) {
    reportError(new Error('Loading investments page without symbol'));
    return <div />;
  }

  return (
    <Page showNavigationBar>
      <Header
        symbol={baseSymbol}
        headingText={baseSymbol}
        category={basicInvestmentQuery.data?.category}
        name={basicInvestmentQuery.data?.name}
      />
      <BottomLineExplanationTakeover
        isShown={isBottomLineExplanationShown}
        onClose={() => setIsBottomLineExplanationShown(false)}
      />
      <FeedbackTakeover isShown={isFeedbackTakeoverShown} onClose={() => setIsFeedbackTakeoverShown(false)} />
      {/* Risk factors route removed */}
      <Route path={`${match.url}/stats`} render={() => <KeyStatsPage baseSymbol={baseSymbol} />} />
      <Route path={`${match.url}/insights`} render={() => <AllInsightsPage symbol={symbol} />} />
      <Route path={`${match.url}/key-events`} render={() => <AllKeyEventsPage symbol={symbol} />} />
      <Route path={`${match.url}/transcript`} render={() => <TranscriptPage symbol={baseSymbol} />} />
      <Route
        exact
        path={match.url}
        render={() =>
          basicInvestmentQuery.isError ? (
            <div
              css={css`
                margin-top: 40vh;
              `}
            >
              <ErrorSection
                text={t('symbol.fetchError', { symbol: baseSymbol })}
                onRetry={() => {
                  basicInvestmentQuery.refetch();
                  chartsQuery.refetch();
                }}
              />
            </div>
          ) : (
            <div>
              <div
                css={css`
                  padding-bottom: ${spacing.deci};
                `}
              >
                <BasicInfo
                  name={basicInvestmentQuery.data?.name}
                  price={
                    showTechnicalChart
                      ? scannedTechnicalElement?.price || basicInvestmentQuery.data?.latestPrice
                      : scannedChartElement?.price || basicInvestmentQuery.data?.latestPrice
                  }
                  changeRatio={
                    showTechnicalChart
                      ? (scannedTechnicalElement?.changeRatio ?? changeRatio)
                      : (scannedChartElement?.changeRatio ?? changeRatio)
                  }
                  dateText={
                    basicInvestmentQuery.data && selectedTimeRange
                      ? showTechnicalChart
                        ? buildTechnicalDateText(selectedChart, selectedTimeRange, scannedTechnicalElement)
                        : buildDateText(selectedChart, selectedTimeRange, scannedChartElement)
                      : ''
                  }
                  currency={basicInvestmentQuery.data?.currency}
                />
              </div>
              <div
                css={css`
                  display: flex;
                  gap: ${spacing.centi};
                  margin-bottom: ${spacing.deci};
                `}
              >
                <RadioButton value="news" checked={!showTechnicalChart} onChange={() => setShowTechnicalChart(false)}>
                  {t('symbol.news')}
                </RadioButton>
                <RadioButton
                  value="technicals"
                  checked={showTechnicalChart}
                  onChange={() => setShowTechnicalChart(true)}
                >
                  {t('symbol.technicals')}
                </RadioButton>
              </div>

              {basicInvestmentQuery.isLoading ? (
                <div
                  css={css`
                    position: fixed;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                  `}
                >
                  <Spinner color={colors.cloud} size="lg" />
                </div>
              ) : (
                <>
                  {showTechnicalChart ? (
                    <>
                      <TechnicalChart
                        data={technicalsQuery.data}
                        timeRange={selectedTimeRange}
                        isLoading={technicalsQuery.isLoading}
                        isFetching={technicalsQuery.isFetching}
                        isError={technicalsQuery.isError}
                        onRetry={() => technicalsQuery.refetch()}
                        onHover={(point) => setScannedTechnicalElement(point)}
                      />

                      <TimeRangeRadios
                        timeRanges={getAvailableTimeRanges()}
                        selectedTimeRange={selectedTimeRange}
                        onRadioChange={(time) => {
                          setSelectedTimeRange(time as AllowedTimeRange);
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Chart
                        css={css`
                          margin-bottom: ${spacing.regular};
                        `}
                        isLoading={chartsQuery.isLoading}
                        isError={chartsQuery.isError}
                        onRetry={() => chartsQuery.refetch()}
                        strokeColor={calculateChangeColor(changeRatio)}
                        charts={chartsQuery.data}
                        timeRange={selectedTimeRange}
                        onSelectNews={(chartElement) => {
                          // Set a timeout to prevent tapping on a link in a wiim from closing the element before the tap can register
                          setTimeout(() => setSelectedNewsChartElement(chartElement), 50);
                        }}
                        selectedNewsElement={selectedNewsChartElement}
                        onScanPrice={(chartElement) => setScannedChartElement(chartElement)}
                        scannedPrice={scannedChartElement}
                        ticker={baseSymbol}
                      />

                      <TimeRangeRadios
                        timeRanges={getAvailableTimeRanges()}
                        selectedTimeRange={selectedTimeRange}
                        onRadioChange={(time) => {
                          setSelectedTimeRange(time as AllowedTimeRange);
                          setSelectedNewsChartElement(undefined);
                          setScannedChartElement(undefined);
                        }}
                      />
                    </>
                  )}
                  {!isSubscribed && !showTechnicalChart && (
                    <div
                      css={css`
                        margin: ${spacing.regular} 0 0 0;
                        display: flex;
                        align-items: center;

                        --chart-color: ${colors.petrolBlue};
                        --shadow-color: #f5f5f5;

                        html[data-theme='dark'] & {
                          --chart-color: ${colors.skyBlue};
                          --shadow-color: ${colors.tungsten};
                        }
                      `}
                    >
                      <div
                        css={css`
                          flex: 0 0 auto;
                          width: 120px;
                        `}
                      >
                        <img src={wiimGraphic} alt="" />
                      </div>
                      <div
                        css={css`
                          flex: 1;
                          min-width: 0;
                          text-align: left;
                        `}
                      >
                        <div
                          css={css`
                            ${fontStyles.fatCat}
                            margin-bottom: ${spacing.centi};
                            color: ${colors.liquorice};
                            html[data-theme='dark'] & {
                              color: ${colors.sugar};
                            }
                          `}
                        >
                          {t('symbol.seeWhyMoving')}
                          <span
                            css={css`
                              display: inline-block;
                              margin-left: ${spacing.milli};
                              background: ${colors.petrolBlue};
                              color: ${colors.sugar};
                              padding: 2px ${spacing.milli};
                              border-radius: 4px;
                              ${fontStyles.flea}
                              text-transform: uppercase;
                              letter-spacing: 0.5px;
                              vertical-align: middle;
                            `}
                          >
                            {t('common.pro')}
                          </span>
                        </div>

                        <div
                          css={css`
                            ${fontStyles.mouse}
                            color: ${colors.silver};
                            margin-bottom: ${spacing.centi};
                            html[data-theme='dark'] & {
                              color: ${colors.sugar};
                            }
                          `}
                        >
                          {['AAPL', 'TSLA'].includes(baseSymbol)
                            ? t('symbol.tryFreeAaplTsla')
                            : t('symbol.subscribeChartNews')}
                        </div>
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            stores.showPaymentModalByPreference('symbol_page');
                          }}
                          css={css`
                            color: ${colors.petrolBlue};
                            text-decoration: none;
                            ${fontStyles.mouse}

                            html[data-theme='dark'] & {
                              color: ${colors.skyBlue};
                            }

                            &:hover {
                              text-decoration: underline;
                            }
                          `}
                        >
                          {t('symbol.learnMore')}
                        </Link>
                      </div>
                    </div>
                  )}
                </>
              )}
              {basicInvestmentQuery.data && (
                <div>
                  <Info {...basicInvestmentQuery.data} />
                  <div
                    css={css`
                      margin-top: ${spacing.deka};
                    `}
                  >
                    {['STOCK', 'ADR', 'INTERNATIONAL_STOCK'].includes(basicInvestmentQuery.data?.category) && (
                      <>
                        <BottomLine symbol={baseSymbol} match={match} />
                        <KeyEvents symbol={baseSymbol} />
                        {basicInvestmentQuery.data?.category !== 'INTERNATIONAL_STOCK' && (
                          <EarningsTranscripts symbol={baseSymbol} />
                        )}
                        <Insights symbol={baseSymbol} />
                      </>
                    )}
                    {['MUTUAL_FUND', 'ETF'].includes(basicInvestmentQuery.data?.category) && (
                      <Overview symbol={baseSymbol} />
                    )}
                  </div>

                  {['MUTUAL_FUND', 'CRYPTO', 'REIT', 'ETF'].includes(basicInvestmentQuery.data?.category) && (
                    <KeyEvents symbol={baseSymbol} />
                  )}

                  {/* Risk factors section removed */}

                  {['MUTUAL_FUND', 'ETF'].includes(basicInvestmentQuery.data?.category) && (
                    <div>
                      <Holdings symbol={baseSymbol} />
                    </div>
                  )}

                  <Related symbol={baseSymbol} />
                  <FeedbackFooter />
                </div>
              )}
            </div>
          )
        }
      />
    </Page>
  );
};

export default SymbolPage;
